import { useEffect, useMemo, useState } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  buildCoachLeaderAccessSnapshot,
  GRACE_PERIOD_DAYS,
} from "@/lib/coachHealth/buildCoachLeaderAccessSnapshot";
import { addDays } from "date-fns";
import {
  COACH_ACTIVE_LEADER_PLAN_TYPE,
  hasCoachBillingPaidAccess,
  hasValidOptionalBillingTimestamps,
} from "@/lib/soloLeaderBilling";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

const RELATIONSHIP_ACCESS_REFRESH_INTERVAL_MS = 30_000;

export interface GatedLeader {
  leaderId: string;
  joinedAt: Date;
  graceDaysLeft: number;
  isHardGated: boolean;
}

export interface LeaderGateResult {
  leaderCount: number;
  leaderLimit: number | null;
  isOverLimit: boolean;
  overBy: number;
  /** Map of leader ID → gating info (grace period, hard/soft) */
  gatedLeaders: Map<string, GatedLeader>;
  /** Quick set check */
  gatedLeaderIds: Set<string>;
  /** Current coach-owned relationship IDs used to fail closed over stale page data. */
  linkedLeaderIds: Set<string>;
  /** Leaders in grace period (soft gate) */
  softGatedCount: number;
  /** Leaders past grace period (hard gate) */
  hardGatedCount: number;
  plan: string;
  isLoading: boolean;
  /** Stable description of the current readable/grace/hard-gated portfolio. */
  accessFingerprint: string;
  /** True while the authoritative relationship snapshot is refetching. */
  isRefreshing: boolean;
  /** True when relationship access cannot be authoritatively read. */
  isError: boolean;
}

/**
 * Determines which leaders are visible vs gated based on subscription limits.
 * - Soft gate (days 1-7): coach sees leader dimmed with countdown, leader sees warning banner
 * - Hard gate (day 8+): coach can't access leader data, leader sees blocked banner
 */
export function useLeaderGate(): LeaderGateResult {
  const { user } = useAuth();
  const { leaderLimit, plan, isLoading: subLoading } = useSubscription();
  const queryClient = useQueryClient();
  const [accessClock, setAccessClock] = useState(() => Date.now());

  const {
    data: leaders,
    dataUpdatedAt: leadersUpdatedAt,
    isError: leadersError,
    isFetching: leadersFetching,
    isLoading: leadersLoading,
  } = useQuery({
    queryKey: ["coach-leader-links-ordered", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("coach_leader_links")
        .select("leader_id, created_at")
        .eq("coach_id", user.id)
        .order("created_at", { ascending: true })
        .order("leader_id", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    refetchInterval: RELATIONSHIP_ACCESS_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const result = useMemo(() => {
    const sortedLeaders = leadersError ? [] : leaders || [];
    const count = sortedLeaders.length;
    const limit = leaderLimit;
    const accessAsOf = new Date(
      Math.max(accessClock, leadersUpdatedAt),
    ).toISOString();
    const isOverLimit = limit !== null && count > limit;
    const overBy = isOverLimit ? count - limit! : 0;

    const access = buildCoachLeaderAccessSnapshot({
      links: sortedLeaders.map((leader) => ({
        leaderId: leader.leader_id,
        createdAt: leader.created_at,
        lifecycle: "active" as const,
      })),
      leaderLimit: limit,
      asOf: accessAsOf,
    });
    const accessFingerprint = [
      `coach:${user?.id ?? "inactive"}`,
      `limit:${limit === null ? "unlimited" : limit}`,
      ...sortedLeaders
        .map((leader) => `${leader.leader_id}:${access.accessByLeaderId.get(leader.leader_id) ?? "unknown"}`)
        .sort(),
    ].join("|");

    return {
      leaderCount: count,
      leaderLimit: limit,
      isOverLimit,
      overBy,
      gatedLeaders: new Map(access.gatedLeaders),
      gatedLeaderIds: new Set(access.gatedLeaderIds),
      linkedLeaderIds: new Set(sortedLeaders.map((leader) => leader.leader_id)),
      softGatedCount: access.softGatedCount,
      hardGatedCount: access.hardGatedCount,
      plan,
      isLoading: subLoading || leadersLoading,
      accessFingerprint,
      isRefreshing: leadersFetching,
      isError: leadersError,
    };
  }, [
    leaders,
    leadersError,
    accessClock,
    leaderLimit,
    plan,
    subLoading,
    leadersFetching,
    leadersLoading,
    leadersUpdatedAt,
    user?.id,
  ]);

  const nextHardGateBoundary = useMemo(() => {
    let earliest: number | null = null;
    for (const gate of result.gatedLeaders.values()) {
      if (gate.isHardGated) continue;
      const boundary = addDays(gate.joinedAt, GRACE_PERIOD_DAYS).getTime();
      if (earliest === null || boundary < earliest) earliest = boundary;
    }
    return earliest;
  }, [result.gatedLeaders]);

  useEffect(() => {
    if (!user?.id || nextHardGateBoundary === null) return;
    const delay = Math.max(1, nextHardGateBoundary - Date.now() + 1);
    const timeout = window.setTimeout(() => {
      setAccessClock(Date.now());
      void queryClient.invalidateQueries({
        queryKey: ["coach-leader-links-ordered", user.id],
      });
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [nextHardGateBoundary, queryClient, user?.id]);

  return result;
}

/**
 * Hook for leader-side: checks if the current leader is gated by their coach's plan.
 */
export function useLeaderGateStatus() {
  const { user } = useAuth();
  const isPreviewUser = user?.id === PREVIEW_LEADER_USER_ID;

  const { data, isLoading } = useQuery({
    queryKey: ["leader-gate-status", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return { isGated: false, graceDaysLeft: null, isHardGated: false };
      }

      // Get the coach link for this leader
      const { data: link, error: linkError } = await supabase
        .from("coach_leader_links")
        .select("coach_id, created_at")
        .eq("leader_id", user.id)
        .maybeSingle();

      if (linkError) return null;
      if (link === null) {
        return { isGated: false, graceDaysLeft: null, isHardGated: false };
      }
      if (!link) return null;

      // Count how many leaders this coach has
      const { count: totalLeaders, error: countError } = await supabase
        .from("coach_leader_links")
        .select("*", { count: "exact", head: true })
        .eq("coach_id", link.coach_id);

      if (countError) return null;

      // Get the coach's subscription to determine their limit
      const { data: sub, error: subError } = await supabase
        .from("subscriptions")
        .select("status, leader_quantity, plan_type, current_period_end, cancel_at_period_end, payment_failure_grace_ends_at")
        .eq("user_id", link.coach_id)
        .maybeSingle();

      if (subError) return null;

      const billingTimestampsValid = hasValidOptionalBillingTimestamps({
        currentPeriodEnd: sub?.current_period_end,
        paymentFailureGraceEndsAt: sub?.payment_failure_grace_ends_at,
      });
      const isPaid = billingTimestampsValid &&
        hasCoachBillingPaidAccess({
          status: sub?.status,
          currentPeriodEnd: sub?.current_period_end,
          cancelAtPeriodEnd: sub?.cancel_at_period_end,
          paymentFailureGraceEndsAt: sub?.payment_failure_grace_ends_at,
        });
      const leaderLimit = isPaid && sub?.plan_type === COACH_ACTIVE_LEADER_PLAN_TYPE
        ? null
        : isPaid
          ? (sub!.leader_quantity >= 11 ? null : 10)
          : 1;

      if (leaderLimit === null || (totalLeaders || 0) <= leaderLimit) {
        return { isGated: false, graceDaysLeft: null, isHardGated: false };
      }

      // This leader might be gated — check if they're beyond the limit
      // Get all leaders ordered by join date to see if this leader is in the gated set
      const { data: allLinks, error: allError } = await supabase
        .from("coach_leader_links")
        .select("leader_id, created_at")
        .eq("coach_id", link.coach_id)
        .order("created_at", { ascending: true })
        .order("leader_id", { ascending: true });

      if (allError || !allLinks) return null;

      const access = buildCoachLeaderAccessSnapshot({
        links: allLinks.map((leader) => ({
          leaderId: leader.leader_id,
          createdAt: leader.created_at,
          lifecycle: "active" as const,
        })),
        leaderLimit,
        asOf: new Date().toISOString(),
      });
      const gate = access.gatedLeaders.get(user.id);
      if (!gate) {
        return { isGated: false, graceDaysLeft: null, isHardGated: false };
      }

      return {
        isGated: true,
        graceDaysLeft: gate.graceDaysLeft,
        isHardGated: gate.isHardGated,
      };
    },
    enabled: !!user?.id && !isPreviewUser,
    staleTime: 5 * 60 * 1000,
  });

  return { ...data, isLoading: !isPreviewUser && isLoading };
}
